import { injectable } from "inversify";
import { io, Socket } from "socket.io-client";
import { IWebSocketApi } from "./IWebSocketApi";
import { AuthenticationEventData, WebSocketEventType } from "./types";

@injectable()
export class WebSocketApi implements IWebSocketApi {
  private socket: Socket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 50;
  private reconnectDelay = 2000;
  private maxReconnectDelay = 30000;
  private eventCallbacks = new Map<string, Set<(payload?: any) => void>>();
  private connectionPromise: Promise<boolean> | null = null;
  private currentPlatformURI = "";
  private currentOrganizationId = "";
  private reconnectTimeoutId: NodeJS.Timeout | null = null;
  private isAuthenticated = false;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private lastPongReceived = Date.now();

  async connect(platformURI: string, organizationId: string): Promise<boolean> {
    // If already connected to the same platform and organization, return true
    if (
      this.socket?.connected &&
      this.currentPlatformURI === platformURI &&
      this.currentOrganizationId === organizationId
    ) {
      return true;
    }

    // If there's an ongoing connection attempt, return that promise
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.currentPlatformURI = platformURI;
    this.currentOrganizationId = organizationId;

    this.connectionPromise = new Promise((resolve, reject) => {
      // Disconnect existing connection if connecting to different platform/org
      if (this.socket?.connected) {
        this.socket.disconnect();
      }

      this.socket = io(platformURI, {
        transports: ["websocket", "polling"],
        timeout: 20000,
        reconnection: false,
        forceNew: true,
        upgrade: true,
        rememberUpgrade: true
      });

      this.socket.on("connect", () => {
        console.log("WebSocket connected to:", platformURI);
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.lastPongReceived = Date.now();
        this.startHeartbeat();

        // Authenticate with the server
        this.socket!.emit("authenticate", { organizationId });
      });

      this.socket.on("authenticated", (data: AuthenticationEventData) => {
        if (data.success) {
          console.log("WebSocket authenticated successfully");
          this.isAuthenticated = true;
          this.connectionPromise = null;
          resolve(true);
        } else {
          console.error("WebSocket authentication failed:", data.error || "Unknown error");
          this.isAuthenticated = false;
          this.connectionPromise = null;
          reject(new Error("Authentication failed"));
        }
      });

      this.socket.on("disconnect", (reason) => {
        console.log("WebSocket disconnected:", reason);
        this.isConnected = false;
        this.isAuthenticated = false;
        this.stopHeartbeat();

        // Always attempt reconnection except for manual disconnections
        if (reason !== "io client disconnect") {
          console.log("Attempting reconnection due to:", reason);
          this.scheduleReconnect();
        }
      });

      this.socket.on("connect_error", (error) => {
        console.error("WebSocket connection error:", error.message || error);
        this.isConnected = false;
        this.isAuthenticated = false;

        if (this.connectionPromise) {
          this.connectionPromise = null;
          reject(error);
        } else {
          // If not initial connection, schedule reconnect
          this.scheduleReconnect();
        }
      });

      this.socket.on("pong", () => {
        this.lastPongReceived = Date.now();
      });

      // Listen to all WebSocket events
      this.socket.on(WebSocketEventType.NEW_NOTIFICATION, (payload: any) => {
        console.log("WebSocket received NEW_NOTIFICATION:", payload);
        this.notifyCallbacks(WebSocketEventType.NEW_NOTIFICATION, payload);
      });

      this.socket.on(WebSocketEventType.NEW_MESSAGE, (payload: any) => {
        console.log("WebSocket received NEW_MESSAGE:", payload);
        this.notifyCallbacks(WebSocketEventType.NEW_MESSAGE, payload);
      });

      this.socket.on(WebSocketEventType.MESSAGE_STATUS_UPDATE, (payload: any) => {
        console.log("WebSocket received MESSAGE_STATUS_UPDATE:", payload);
        this.notifyCallbacks(WebSocketEventType.MESSAGE_STATUS_UPDATE, payload);
      });

      this.socket.on(WebSocketEventType.NEW_CONVERSATION, (payload: any) => {
        console.log("WebSocket received NEW_CONVERSATION:", payload);
        this.notifyCallbacks(WebSocketEventType.NEW_CONVERSATION, payload);
      });

      this.socket.on(WebSocketEventType.CONVERSATION_UPDATE, (payload: any) => {
        console.log("WebSocket received CONVERSATION_UPDATE:", payload);
        this.notifyCallbacks(WebSocketEventType.CONVERSATION_UPDATE, payload);
      });
    });

    return this.connectionPromise;
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(
        `Max reconnection attempts reached (${this.maxReconnectAttempts}). Stopping reconnection attempts.`
      );
      return;
    }

    this.reconnectAttempts += 1;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    console.log(
      `Scheduling reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`
    );

    this.reconnectTimeoutId = setTimeout(() => {
      this.attemptReconnect();
    }, delay);
  }

  private async attemptReconnect(): Promise<void> {
    if (this.isConnected || !this.currentPlatformURI || !this.currentOrganizationId) {
      return;
    }

    console.log(`Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);

    try {
      if (this.socket) {
        this.socket.disconnect();
        this.socket.removeAllListeners();
      }

      await this.connect(this.currentPlatformURI, this.currentOrganizationId);
      console.log("Reconnection successful");
    } catch (error) {
      console.error("Reconnection failed:", error);

      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect();
      }
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.connected) {
        const timeSinceLastPong = Date.now() - this.lastPongReceived;

        if (timeSinceLastPong > 35000) {
          console.warn("Heartbeat timeout detected, forcing reconnection");
          this.socket.disconnect();
          return;
        }

        this.socket.emit("ping");
      }
    }, 30000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  on(event: string, callback: (payload?: any) => void): void {
    if (!this.eventCallbacks.has(event)) {
      this.eventCallbacks.set(event, new Set());
    }
    this.eventCallbacks.get(event)!.add(callback);
  }

  off(event: string, callback: (payload?: any) => void): void {
    const callbacks = this.eventCallbacks.get(event);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.eventCallbacks.delete(event);
      }
    }
  }

  private notifyCallbacks(event: string, payload?: any): void {
    const callbacks = this.eventCallbacks.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(payload);
        } catch (error) {
          console.error(`Error in WebSocket callback for event ${event}:`, error);
        }
      });
    }
  }

  disconnect(): void {
    console.log("Manually disconnecting WebSocket");

    // Clear reconnection timeout
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    // Stop heartbeat
    this.stopHeartbeat();

    // Disconnect socket
    if (this.socket) {
      this.socket.disconnect();
      this.socket.removeAllListeners();
      this.socket = null;
    }

    // Reset state
    this.isConnected = false;
    this.isAuthenticated = false;
    this.reconnectAttempts = 0;
    this.eventCallbacks.clear();
    this.connectionPromise = null;
    this.currentPlatformURI = "";
    this.currentOrganizationId = "";
  }

  getConnectionStatus(): boolean {
    return this.isConnected && this.isAuthenticated && this.socket?.connected === true;
  }

  getReconnectionInfo(): { attempts: number; maxAttempts: number; isReconnecting: boolean } {
    return {
      attempts: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
      isReconnecting: this.reconnectTimeoutId !== null
    };
  }

  resetReconnectionAttempts(): void {
    this.reconnectAttempts = 0;
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }
}
